import ActionDialog from "@dashboard/components/ActionDialog/ActionDialog";
import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import { type AtticErrorFragment } from "@dashboard/graphql";
import { Box, Input, Text } from "@saleor/macaw-ui-next";
import type React from "react";
import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { getAtticAssetErrorMessage } from "../../errors";
import { directoryDialogMessages } from "../../messages";
import { getDirectoryErrorTarget } from "../../utils";

export type AtticAssetDirectoryDialogMode =
  | { type: "create" }
  | { type: "rename"; name: string; slug: string };

export interface AtticAssetDirectoryDialogProps {
  open: boolean;
  mode: AtticAssetDirectoryDialogMode;
  errors: AtticErrorFragment[];
  confirmButtonState: ConfirmButtonTransitionState;
  /** The create or rename is in flight: a second Enter or click sends nothing. */
  submitting: boolean;
  onClose: () => void;
  /** Resolves when the call is over, whatever it answered. */
  onSubmit: (values: AtticAssetDirectoryDialogValues) => Promise<unknown>;
}

export interface AtticAssetDirectoryDialogValues {
  name: string;
  slug: string;
}

/** Add a directory (name, optional folder) or rename one (name only: the folder never changes). */
export const AtticAssetDirectoryDialog = ({
  open,
  mode,
  errors,
  confirmButtonState,
  submitting,
  onClose,
  onSubmit,
}: AtticAssetDirectoryDialogProps) => {
  const intl = useIntl();
  const initialName = mode.type === "rename" ? mode.name : "";
  const { control, handleSubmit, reset, watch } = useForm<AtticAssetDirectoryDialogValues>({
    defaultValues: { name: initialName, slug: "" },
  });
  const name = watch("name");
  const slug = watch("slug");

  // Each opening starts from the directory as it is.
  useEffect(() => {
    if (open) {
      reset({ name: initialName, slug: "" });
    }
  }, [open, initialName, reset]);

  // react-hook-form does not stop a second submit while the first one runs,
  // and two Enters in one frame land before any state can re-render: this is
  // set at once, and cleared when the call is over.
  const sent = useRef(false);

  // Every refusal arrives with `field: null`; its code says where it belongs.
  const target = (error: AtticErrorFragment) =>
    getDirectoryErrorTarget(error.code, mode.type, !!slug.trim());
  const nameError = errors.find(error => target(error) === "name");
  const slugError = errors.find(error => target(error) === "slug");
  const otherErrors = errors.filter(error => error !== nameError && error !== slugError);
  const submit = handleSubmit(async values => {
    if (!values.name.trim() || submitting || sent.current) {
      return;
    }

    sent.current = true;

    try {
      await onSubmit({ name: values.name.trim(), slug: values.slug.trim() });
    } finally {
      sent.current = false;
    }
  });
  // Enter in either field submits, as the confirm button would.
  const submitOnEnter = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      void submit();
    }
  };
  const describe = (error: AtticErrorFragment) =>
    getAtticAssetErrorMessage(error, intl, "directory");

  return (
    <ActionDialog
      open={open}
      title={intl.formatMessage(
        mode.type === "create"
          ? directoryDialogMessages.createTitle
          : directoryDialogMessages.renameTitle,
      )}
      confirmButtonLabel={intl.formatMessage(
        mode.type === "create" ? directoryDialogMessages.create : directoryDialogMessages.save,
      )}
      confirmButtonState={confirmButtonState}
      disabled={!name.trim() || submitting}
      onClose={onClose}
      onConfirm={() => void submit()}
    >
      <Box display="flex" flexDirection="column" gap={4}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              {...field}
              autoFocus
              label={intl.formatMessage(directoryDialogMessages.name)}
              onKeyDown={submitOnEnter}
              error={!!nameError}
              helperText={nameError ? describe(nameError) : undefined}
              data-test-id="attic-asset-directory-name"
            />
          )}
        />
        {mode.type === "create" ? (
          <Controller
            control={control}
            name="slug"
            render={({ field }) => (
              <Input
                {...field}
                label={intl.formatMessage(directoryDialogMessages.slug)}
                onKeyDown={submitOnEnter}
                error={!!slugError}
                helperText={
                  slugError
                    ? describe(slugError)
                    : intl.formatMessage(directoryDialogMessages.slugHelp)
                }
                data-test-id="attic-asset-directory-slug"
              />
            )}
          />
        ) : (
          <Text size={2} color="default2">
            {intl.formatMessage(directoryDialogMessages.renameHelp, { slug: mode.slug })}
          </Text>
        )}
        {otherErrors.map((error, index) => (
          <Text key={index} size={2} color="critical1" data-test-id="attic-asset-directory-error">
            {describe(error)}
          </Text>
        ))}
      </Box>
    </ActionDialog>
  );
};

export default AtticAssetDirectoryDialog;
